import { AlertCircle, Camera, Loader2, Save, Trash2, User } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { AppShell } from "components/AppShell";
import { EmptyState, LoadingRows } from "components/states";
import { ThemeSwitch } from "components/ThemeSwitch";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";

import { CompanyField } from "../business-units/CompanyField";
import { NotificationSettingsCard } from "../notifications/PushStatus";

import { normalizeLinkedInUrl } from "./linkedin";
import type { ProfileUpdate } from "./schemas";
import { SharingSettingsCard } from "./SharingSettingsCard";
import { AVATAR_CONTENT_TYPES, useAvatarUpload } from "./use-avatar-upload";
import {
  useSaveAvatar,
  useUpdateProfile,
  useUserProfile,
} from "./use-user-profile";

export function ProfilePage({
  uid,
  email,
  signOut,
}: {
  uid: string;
  email: string | null;
  signOut: () => void;
}) {
  const { state, reload } = useUserProfile(uid);
  const identity = { email };

  if (state.status === "loading") {
    return (
      <AppShell identity={identity} onSignOut={signOut} title="Profile">
        <LoadingRows rows={5} />
      </AppShell>
    );
  }

  if (state.status === "not-provisioned") {
    return (
      <AppShell identity={identity} onSignOut={signOut} title="Profile">
        <EmptyState
          icon={User}
          title="Account not ready"
          testId="profile-not-provisioned"
        >
          Your account has not been provisioned yet. Please check back later.
        </EmptyState>
      </AppShell>
    );
  }

  if (state.status === "error") {
    return (
      <AppShell identity={identity} onSignOut={signOut} title="Profile">
        <EmptyState
          icon={AlertCircle}
          title="Could not load profile"
          testId="profile-error"
        >
          {state.error}
        </EmptyState>
      </AppShell>
    );
  }

  return (
    <AppShell identity={identity} onSignOut={signOut} title="Profile">
      <ProfileForm uid={uid} profile={state.profile} onSaved={reload} />
      <SharingSettingsCard
        uid={uid}
        profile={state.profile}
        className="mx-auto mt-6 max-w-2xl"
      />
      <NotificationSettingsCard className="mx-auto mt-6 max-w-2xl" />
      <AppearanceCard className="mx-auto mt-6 max-w-2xl" />
    </AppShell>
  );
}

/**
 * Which colour scheme the participant side shows. The choice lives on the device
 * (see features/theme/theme.ts), not in the profile, so it needs no save button.
 */
function AppearanceCard({ className }: { className?: string }) {
  return (
    <Card className={className} data-testid="appearance-card">
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
      </CardHeader>
      <CardContent>
        <ThemeSwitch />
      </CardContent>
    </Card>
  );
}

function ProfileForm({
  uid,
  profile,
  onSaved,
}: {
  uid: string;
  profile: {
    name: string;
    jobTitle?: string;
    company?: string;
    avatar?: string;
    bio?: string;
    linkedinUrl?: string;
  };
  onSaved: () => void;
}) {
  const [form, setForm] = useState<ProfileUpdate>({
    name: profile.name ?? "",
    jobTitle: profile.jobTitle ?? "",
    company: profile.company ?? "",
    avatar: profile.avatar ?? "",
    bio: profile.bio ?? "",
    linkedinUrl: profile.linkedinUrl ?? "",
  });

  const { save, saving, error: saveError } = useUpdateProfile(uid);
  const {
    upload,
    discardUnsaved,
    uploading,
    progress,
    error: uploadError,
  } = useAvatarUpload(uid);
  const saveAvatar = useSaveAvatar(uid);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [linkedinError, setLinkedinError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleChange = (field: keyof ProfileUpdate, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // The photo is saved as soon as it is uploaded or removed, independently of
  // the rest of the form, so it cannot be lost by leaving without pressing Save.
  // The photo it replaces is left in Storage: see useAvatarUpload.
  const replaceAvatar = async (next: string): Promise<boolean> => {
    setAvatarError(null);
    const failure = await saveAvatar(next);
    if (failure) {
      setAvatarError(failure);
      return false;
    }
    handleChange("avatar", next);
    return true;
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared so choosing the same file again still fires a change.
    e.target.value = "";
    if (!file) return;

    const url = await upload(file);
    if (!url) return;
    if (await replaceAvatar(url)) {
      toast.success("Photo updated");
    } else {
      void discardUnsaved(url);
    }
  };

  const handleRemoveAvatar = async () => {
    if (await replaceAvatar("")) toast.success("Photo removed");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    const linkedin = normalizeLinkedInUrl(form.linkedinUrl ?? "");
    if ("error" in linkedin) {
      setLinkedinError(linkedin.error);
      return;
    }
    setLinkedinError(null);
    const next = { ...form, linkedinUrl: linkedin.value };
    setForm(next);

    const ok = await save(next);
    if (ok) {
      toast.success("Profile saved");
      onSaved();
    }
  };

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="mx-auto max-w-2xl"
      data-testid="profile-form"
    >
      <Card>
        <CardHeader>
          <CardTitle>Your profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar */}
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="group relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              disabled={uploading}
              aria-label="Upload profile photo"
              data-testid="avatar-button"
            >
              {form.avatar ? (
                <img
                  src={form.avatar}
                  alt="Your profile photo"
                  data-testid="avatar-image"
                  className="h-full w-full object-cover"
                />
              ) : (
                <User className="h-8 w-8 text-muted-foreground" />
              )}
              {/* The camera is a hover hint; the upload state shows on any
                  device, since a phone never hovers. */}
              <span
                className={`absolute inset-0 flex items-center justify-center bg-hb-dusk-deep/60 transition-opacity ${
                  uploading
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100"
                }`}
              >
                {uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                ) : (
                  <Camera className="h-5 w-5 text-white" />
                )}
              </span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept={AVATAR_CONTENT_TYPES.join(",")}
              className="hidden"
              onChange={(e) => void handleAvatarChange(e)}
              data-testid="avatar-input"
            />
            <div className="min-w-0 space-y-2 text-sm text-muted-foreground">
              <div>
                <p>Your photo appears on your attendee card at every event.</p>
                <p className="text-xs">PNG, JPG or WebP, max 2 MB</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="max-sm:h-11"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="avatar-choose"
                >
                  <Camera className="mr-2 h-4 w-4" />
                  {form.avatar ? "Change photo" : "Upload photo"}
                </Button>
                {form.avatar && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="max-sm:h-11"
                    onClick={() => void handleRemoveAvatar()}
                    disabled={uploading}
                    data-testid="avatar-remove"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Remove
                  </Button>
                )}
              </div>
              {/* Always mounted, so a screen reader announces the change. */}
              <p className="text-xs empty:hidden" role="status">
                {uploading && (
                  <span data-testid="avatar-progress">
                    Uploading photo… {progress ?? 0}%
                  </span>
                )}
              </p>
              {(uploadError ?? avatarError) && (
                <p
                  className="text-xs text-destructive-text"
                  role="alert"
                  data-testid="avatar-error"
                >
                  {uploadError ?? avatarError}
                </p>
              )}
            </div>
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
              data-testid="profile-name"
            />
          </div>

          {/* Job Title */}
          <div className="space-y-1.5">
            <Label htmlFor="jobTitle">Job title</Label>
            <Input
              id="jobTitle"
              value={form.jobTitle ?? ""}
              onChange={(e) => handleChange("jobTitle", e.target.value)}
              data-testid="profile-job-title"
            />
          </div>

          {/* Company */}
          <div className="space-y-1.5">
            <Label htmlFor="company">Company</Label>
            <CompanyField
              id="company"
              testId="profile-company"
              value={form.company ?? ""}
              onChange={(next) => handleChange("company", next)}
            />
          </div>

          {/* Bio */}
          <div className="space-y-1.5">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={form.bio ?? ""}
              onChange={(e) => handleChange("bio", e.target.value)}
              rows={3}
              data-testid="profile-bio"
            />
          </div>

          {/* LinkedIn */}
          <div className="space-y-1.5">
            <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
            <Input
              id="linkedinUrl"
              type="text"
              inputMode="url"
              autoComplete="url"
              value={form.linkedinUrl ?? ""}
              onChange={(e) => handleChange("linkedinUrl", e.target.value)}
              placeholder="https://www.linkedin.com/in/your-name"
              aria-invalid={linkedinError ? true : undefined}
              aria-describedby={linkedinError ? "linkedinUrl-error" : undefined}
              data-testid="profile-linkedin"
            />
            {linkedinError && (
              <p
                id="linkedinUrl-error"
                className="text-xs text-destructive-text"
                role="alert"
              >
                {linkedinError}
              </p>
            )}
          </div>

          {saveError && (
            <p className="text-sm text-destructive-text" role="alert">
              {saveError}
            </p>
          )}

          <Button
            variant="cta"
            type="submit"
            disabled={saving}
            className="max-sm:h-11"
            data-testid="profile-save"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save
          </Button>
        </CardContent>
      </Card>
    </form>
  );
}
